/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { BaseController } from '../../BaseController';
import { ListController } from '../../ListController';

class AlarmListController extends ListController {
    /** @ngInject */
    constructor(alarmService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService, configuration,
        domainService, siteService, equipmentService, filterService, $scope, dialogService, counterService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.alarmService = alarmService;
        this.domainService = domainService;
        this.siteService = siteService;
        this.equipmentService = equipmentService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.counterService = counterService;
        this.$scope = $scope;
        // mdDataTable doesn't support ES6 syntax with $ctrl variable in templates. That's why we must attach the reload
        // callback to the scope with an arrow function so as it can be called by this component with the correct 'this'
        // binding.
        this.$scope.reload = () => this.reload();
        this.$log.debug('Controller \'AlarmList\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.paging.sort = '-creationDate';
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
            this.reload();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.equipmentFilter = Filter.equalFilterWithValue('equipment.id', this.$stateParams.filter.equipmentId,
            'model.equipment.equipment', 'SELECT', []);

        this.siteFilter = Filter.equalFilterWithValue('equipment.site.id', this.$stateParams.filter.siteId,
            'model.site.site', 'SELECT_AUTOCOMPLETE', [], siteId => this.getFilterEquipments(siteId));

        this.domainFilter = Filter.equalFilterWithValue('equipment.site.domain.id', this.$stateParams.filter.domainId,
            'model.domain.domain', 'SELECT', [], domainId => this.getFilterSites(domainId));

        let alarmStatuses;
        let actionRequest;
        if (this.$stateParams.filter.equipmentId === undefined) {
            // all alarms
            alarmStatuses = ['PENDING'];
            actionRequest = false;
        } else {
            // alarms for 1 equipment
            alarmStatuses = [];
            actionRequest = undefined;
        }

        this.filters.push(
            Filter.equalFilterEmpty('id', 'ui.id', 'NUMBER'),
            Filter.alarmStatusFilter(alarmStatuses),
            Filter.nullFilterWithOptions('actionRequest', 'model.actionrequest.actionrequest', actionRequest, [
                {id: true, labelKey: 'ui.alarm.withactionrequest'},
                {id: false, labelKey: 'ui.alarm.withoutactionrequest'}
            ]),
            this.domainFilter,
            this.siteFilter,
            this.equipmentFilter,
            Filter.greaterOrEqualDateTimeFilter('creationDate', undefined, 'ui.alarm.createdafter'),
            Filter.lowerDateTimeFilter('creationDate', undefined, 'ui.alarm.createdbefore')
        );
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        this.commands.push(
            Command.stateCommand('ui.alarm.create', 'images/svg/add.svg', 'gmao.alarm.create',
                ['ADMIN', 'ALARM_CREATE']),
            Command.handlerCommand(undefined, 'images/svg/reset.svg', () => this.reload())
        );
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains()
            .then(() => this.getFilterSites(this.domainFilter.value))
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of sites for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of sites.
     */
    getFilterSites(domainId) {
        return this.filterService.getOrClearFilterOptions(this.siteService, this.siteFilter, 'name',
            [Filter.equalFilter('domain.id', domainId)], domainId)
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of equipments for a given site.
     *
     * @param {Number} [siteId] Site ID, may be undefined if no site is selected.
     * @return {Object[]} Promise providing an array of equipments.
     */
    getFilterEquipments(siteId) {
        return this.filterService.getOrClearFilterOptions(this.equipmentService, this.equipmentFilter, 'name',
            [Filter.equalFilter('site.id', siteId)], siteId);
    }

    /**
     * Reloads the list of alarms.
     *
     * @return {Promise} A promise providing the list of alarms.
     */
    reload() {
        this.counterService.reloadCount();
        return this.handleRestServiceCall(
            this.alarmService.searchEntities({
                params: this.filterService.buildPagingParams(this.paging),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.model.data = data.content;
                this.model.totalElements = data.totalElements;
            }
        );
    }

    showCloseAlarmConfirmationDialog(alarm) {
        return this.dialogService.showConfirmationDialogWithMessage('ui.alarm.closeconfirmation').then(
            confirmed => {
                if (confirmed) {
                    this.closeAlarm(alarm).then(() => this.reload());
                }
            }
        );
    }

    closeAlarm(alarm) {
        let promise;
        if (alarm) {
            promise = this.handleRestServiceCall(this.alarmService.updateEntity({id: alarm.id}), () => {
                return this.notificationService.pushMessage('ui.alarm.closed',
                    this.configuration.NOTIFIER_LEVEL_INFO);
            });
        }
        return promise;
    }
}

export const alarmListComponent = {
    template: require('./alarm-list.html'),
    controller: AlarmListController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/alarm/component/AlarmList.js